// Builds the "Review Summary" panel's group tree from the review payload -
// the EXW equivalent of Strategy Canvas's buildReviewSummary
// (StrategyCanvasSummary.js), simplified because EXW's 4 sections are
// already the top-level grouping (no band > section nesting to walk, per
// D4 - StrategyCanvasStructure.js's buildBands has nothing to port here).
//
// ITEM_KIND/countItems/countWhere/filterGroups are imported rather than
// reimplemented: SummaryPanel.js's SummaryItem hardcodes comparisons against
// ITEM_KIND's exact string values, so a local copy with different values
// would silently stop matching; the count/filter helpers are pure array
// utilities with no Canvas-specific logic, safe to share as-is.
import {
  ITEM_KIND,
  countItems,
  countWhere,
  filterGroups,
} from "../../StrategyCanvas/StrategyCanvasSummary";
import { getSectionColor } from "./ExecutionBlueprintReviewHelpers";
import { REVIEW_STATUS } from "../_redux/ExecutionBlueprintUrls";

export { ITEM_KIND, countItems, countWhere, filterGroups };

const hasText = (comment) =>
  !!(comment?.comment_text && comment.comment_text.trim());

// One group per section (in `sequence` order) plus a trailing "whole
// blueprint" group for the overall comment - same shape SummaryGroups
// (SummaryPanel.js) expects: { key, band, name, color, items }.
export const buildReviewSummary = (review) => {
  if (!review) return [];
  const sections = [...(review.sections || [])].sort(
    (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
  );
  const groups = [];

  sections.forEach((section, index) => {
    const items = [];

    // Section comment first - it frames the items underneath it, same
    // ordering Canvas uses.
    (section.comments || []).filter(hasText).forEach((comment) => {
      items.push({
        id: `section-${comment.coach_comment_id}`,
        kind: ITEM_KIND.SECTION,
        title: section.label,
        status: null,
        comments: [comment],
      });
    });

    (section.items || []).forEach((item) => {
      const comments = (item.comments || []).filter(hasText);
      const flagged = item.review_status === REVIEW_STATUS.TO_DISCUSS;
      // Only surfaced here if there's something to say about it - an
      // accepted item with no comment doesn't need a line in the summary.
      if (!flagged && !comments.length) return;
      items.push({
        id: `item-${item.execution_blueprint_item_id}`,
        kind: ITEM_KIND.BLOCK,
        title: item.label,
        status: item.review_status,
        comments,
      });
    });

    if (items.length) {
      groups.push({
        key: section.section_key,
        bandKey: section.section_key,
        sectionKey: section.section_key,
        band: null,
        name: section.label,
        color: getSectionColor(index),
        items,
      });
    }
  });

  const overall = (review.overall_comments || []).filter(hasText);
  if (overall.length) {
    groups.push({
      key: "__overall__",
      bandKey: null,
      sectionKey: null,
      band: null,
      name: "Whole blueprint",
      color: "core",
      items: [
        {
          id: `overall-${overall[0].coach_comment_id}`,
          kind: ITEM_KIND.OVERALL,
          title: "Overall assessment",
          status: null,
          comments: overall,
        },
      ],
    });
  }

  return groups;
};
